import { ed25519 } from '@noble/curves/ed25519';
import {
  decodeBase64Url,
  encodeBase64Url,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  sha256,
  unpackEncryptedBlob,
  decryptAesGcm,
} from './primitives';
import { deriveInfoShareContentKey } from './guestPassword';

export interface InfoShareFragment {
  v: number;
  fs: string;
  ck?: string;
  salt?: string;
  mb: string;
}

export interface InfoShareManifestPlaintext {
  text: string;
  attachments: Array<{
    fileId: string;
    blobId: string;
    encryptedName: string;
    plaintextSha256: string;
    size: number;
    chunkPlaintextBytes?: number;
    chunkCiphertextSizes?: number[];
  }>;
}

export function parseFragmentFromLocation(): InfoShareFragment | null {
  const token = window.location.pathname.match(/^\/i\/([^/]+)$/)?.[1];
  if (!token) {
    return null;
  }

  return resolveFragmentForToken(token);
}

const FRAGMENT_SESSION_KEY = 'shareweb:infoFragment:';

export function resolveFragmentForToken(token: string): InfoShareFragment | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) {
    const fromHash = decodeFragment(hash);
    if (fromHash) {
      cacheFragment(token, fromHash);
      history.replaceState(null, '', window.location.pathname + window.location.search);
      return fromHash;
    }
  }

  return loadCachedFragment(token);
}

function decodeFragment(hash: string): InfoShareFragment | null {
  try {
    const json = new TextDecoder().decode(decodeBase64Url(hash));
    const fragment = JSON.parse(json) as InfoShareFragment;
    if (fragment.v !== 1 || !fragment.fs || !fragment.mb) {
      return null;
    }

    if (!fragment.ck && !fragment.salt) {
      return null;
    }

    return fragment;
  } catch {
    return null;
  }
}

function cacheFragment(token: string, fragment: InfoShareFragment): void {
  sessionStorage.setItem(`${FRAGMENT_SESSION_KEY}${token}`, JSON.stringify(fragment));
}

function loadCachedFragment(token: string): InfoShareFragment | null {
  const cached = sessionStorage.getItem(`${FRAGMENT_SESSION_KEY}${token}`);
  if (!cached) {
    return null;
  }

  try {
    const fragment = JSON.parse(cached) as InfoShareFragment;
    if (fragment.v !== 1 || !fragment.fs || !fragment.mb) {
      return null;
    }

    if (!fragment.ck && !fragment.salt) {
      return null;
    }

    return fragment;
  } catch {
    return null;
  }
}

export function buildFragment(fragment: InfoShareFragment): string {
  const json = JSON.stringify(fragment);
  return encodeBase64Url(new TextEncoder().encode(json));
}

export function deriveProofKeys(proofSeed: Uint8Array): {
  secretKey: Uint8Array;
  publicKey: Uint8Array;
} {
  return ed25519.keygen(proofSeed);
}

export async function encryptManifest(
  contentKey: Uint8Array,
  manifest: InfoShareManifestPlaintext,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(contentKey);
  const plaintext = new TextEncoder().encode(JSON.stringify(manifest));
  const encrypted = await encryptAesGcm(aesKey, plaintext);
  return packEncryptedBlob(encrypted);
}

export async function decryptManifest(
  contentKey: Uint8Array,
  manifestBlob: Uint8Array,
): Promise<InfoShareManifestPlaintext> {
  const aesKey = await importAesKey(contentKey);
  const plaintext = await decryptAesGcm(aesKey, unpackEncryptedBlob(manifestBlob));
  return JSON.parse(new TextDecoder().decode(plaintext)) as InfoShareManifestPlaintext;
}

export async function resolveContentKey(
  fragment: InfoShareFragment,
  password?: string,
): Promise<Uint8Array | null> {
  if (fragment.ck) {
    return decodeBase64Url(fragment.ck);
  }

  if (!fragment.salt || !password) {
    return null;
  }

  const salt = decodeBase64Url(fragment.salt);
  return deriveInfoShareContentKey(password, salt);
}

export async function signGateChallenge(
  secretKey: Uint8Array,
  nonceBase64Url: string,
  gateResponseText: string,
): Promise<string> {
  const nonce = decodeBase64Url(nonceBase64Url);
  const gateHash = await sha256(new TextEncoder().encode(gateResponseText));
  const message = new Uint8Array(nonce.length + gateHash.length);
  message.set(nonce, 0);
  message.set(gateHash, nonce.length);
  const signature = ed25519.sign(message, secretKey);
  return encodeBase64Url(signature);
}

export function createShareSecrets(): {
  proofSeed: Uint8Array;
  contentKey: Uint8Array;
  proofPublicKey: Uint8Array;
  proofSecretKey: Uint8Array;
} {
  const proofSeed = randomBytes(32);
  const contentKey = randomBytes(32);
  const { secretKey, publicKey } = deriveProofKeys(proofSeed);
  return {
    proofSeed,
    contentKey,
    proofPublicKey: publicKey,
    proofSecretKey: secretKey,
  };
}
